// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Container, Grid } from '@mui/material';

import { contactDetails, routes } from '../../config/constant';
import { AuthContext } from '../../context/AuthContext';
import { getStats } from '../../slices/commonSlice/landingStatSlice';
import { getUserLeaderboard } from '../../slices/leaderboardSlice/userLeaderboard';
import { getLeaderboard } from '../../slices/publicSlice/leaderboardSlice';
import { RootState, useAppDispatch, useAppSelector } from '../../slices/store';
import CertificationCard from './CertificationCard';
import ExamCard from './ExamCard';
import Leaderboard from './Leaderboard';
import UserLeaderboard from './UserLeaderboard';
import WhatsNewCard from './WhatsNewCard';

const GuideCards = () => {
  const dispatch = useAppDispatch();
  const contactState = useAppSelector((state: RootState) => state.contact);
  const landingStats = useAppSelector((state: RootState) => state.landingStats);
  const leaderboard = useAppSelector((state: RootState) => state.leaderboard);
  const userLeaderboard = useAppSelector((state: RootState) => state.userLeaderboard);
  const authContext = useContext(AuthContext);
  const auth = useAppSelector((state: RootState) => state.auth);
  let navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (auth.status === "success" && contactState.state === "success") {
        await dispatch(
          getStats({
            contactId: contactState.response.id,
            userCertificationId: contactState.response.certificationId,
          })
        );
        if (!contactState.response.isInternalUser) {
          await dispatch(
            getUserLeaderboard(contactState.response.certificationId)
          );
        }
      }
      if (!auth.isAuthenticated) {
        dispatch(getLeaderboard());
      }
    };

    fetchData();
  }, [contactState.state, auth.isAuthenticated]);

  return (
    <Container sx={{ pb: 6, mt: auth.isAuthenticated ? -14 : 0 }}>
      <Grid
        container
        spacing={5}
        alignItems="flex-start"
        justifyContent={"center"}
      >
        <Grid item xs={12} md={4}>
          {auth.isAuthenticated ? (
            <>
              {contactState.response.isInternalUser && <WhatsNewCard />}
              {!contactState.response.isInternalUser && (
                <ExamCard
                  auth={auth}
                  landingStats={landingStats}
                  contactDetails={contactDetails}
                  routes={routes}
                  authContext={authContext}
                />
              )}
            </>
          ) : (
            <ExamCard
              auth={auth}
              landingStats={landingStats}
              contactDetails={contactDetails}
              routes={routes}
              authContext={authContext}
            />
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {auth.isAuthenticated && contactState.response.isInternalUser ? (
            <ExamCard
              auth={auth}
              landingStats={landingStats}
              contactDetails={contactDetails}
              routes={routes}
              authContext={authContext}
            />
          ) : (
            <CertificationCard
              auth={auth}
              landingStats={landingStats}
              contactDetails={contactDetails}
              routes={routes}
              authContext={authContext}
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          {auth.isAuthenticated ? (
            <>
              {contactState.response.isInternalUser ? (
                <CertificationCard
                  auth={auth}
                  landingStats={landingStats}
                  contactDetails={contactDetails}
                  routes={routes}
                  authContext={authContext}
                />
              ) : (
                <UserLeaderboard data={userLeaderboard} />
              )}
            </>
          ) : (
            <Leaderboard data={leaderboard} />
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default GuideCards;
